import {
  openSession,
  loginWithGithub,
  loginWithMicrosoft,
  captureTokenFromHash,
  captureAuthOutcome,
  me,
  getToken,
  clearToken,
  listSessions,
  type SessionListItem,
  type ProductAccess,
} from "./api.js";
import { el, describeError } from "./dom.js";
import { saveSession, clearSession } from "./state.js";
import { buildSessionRow } from "./session-row.js";

const setupForm = el<HTMLFormElement>("setup-form");
const productSelect = el<HTMLSelectElement>("product-id");
const targetVersion = el<HTMLInputElement>("target-version");
const openBtn = el<HTMLButtonElement>("open-session");
const setupStatus = el<HTMLParagraphElement>("setup-status");
const githubLogin = el<HTMLButtonElement>("github-login");
const microsoftLogin = el<HTMLButtonElement>("microsoft-login");
const authRow = el<HTMLDivElement>("auth-row");
const signedIn = el<HTMLElement>("signed-in");
const signOutBtn = el<HTMLButtonElement>("sign-out");
const findSessionsBtn = el<HTMLButtonElement>("find-sessions");
const sessionList = el<HTMLUListElement>("session-list");
const createProductRow = el<HTMLParagraphElement>("create-product-row");

const OWNER_IMPLIES_ALL = true; // owners hold every permission (see SPEC §2)

// Identity + product access come ONLY from sign-in (/me) - no manual entry.
let signedInEmail = "";
let products: ProductAccess[] = [];

function setStatus(text: string, isError = false): void {
  setupStatus.textContent = text;
  setupStatus.classList.toggle("error", isError);
}

/** The member's rights + product display name, from the products /me returned. */
function resolveAccess(product: string): {
  productName?: string;
  canViewDocs: boolean;
  canManageDocs: boolean;
  isOwner: boolean;
} {
  const access = products.find((p) => p.id === product);
  if (!access) return { canViewDocs: false, canManageDocs: false, isOwner: false };
  const owner = OWNER_IMPLIES_ALL && access.is_owner;
  const canManageDocs = owner || access.permissions.includes("manage_documents");
  const canViewDocs = canManageDocs || access.permissions.includes("view_documents");
  return { productName: access.name, canViewDocs, canManageDocs, isOwner: access.is_owner };
}

setupForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  const product = productSelect.value;
  if (!signedInEmail) {
    setStatus("Sign in with GitHub to continue.", true);
    return;
  }
  if (!product) {
    setStatus("Select a product.", true);
    return;
  }

  openBtn.disabled = true;
  setStatus("Opening session…");
  try {
    const version = targetVersion.value.trim() || null;
    const res = await openSession(product, version);
    const access = resolveAccess(product);

    saveSession({
      id: res.session_id,
      email: signedInEmail,
      product,
      productName: access.productName,
      version: res.target_version,
      canViewDocs: access.canViewDocs,
      canManageDocs: access.canManageDocs,
      isOwner: access.isOwner,
    });
    location.href = "chat.html";
  } catch (err) {
    setStatus(describeError(err), true);
  } finally {
    openBtn.disabled = false;
  }
});

// ---- Resume a previous session ----

/** A clickable row that resumes the given session on click. */
function sessionRow(s: SessionListItem, product: string): HTMLLIElement {
  return buildSessionRow({
    item: s,
    onResume: () => {
      const access = resolveAccess(product);
      saveSession({
        id: s.id,
        email: signedInEmail,
        product,
        productName: access.productName,
        version: s.target_version,
        canViewDocs: access.canViewDocs,
        canManageDocs: access.canManageDocs,
        isOwner: access.isOwner,
      });
      location.href = "chat.html";
    },
  });
}

findSessionsBtn.addEventListener("click", async () => {
  const product = productSelect.value;
  if (!signedInEmail || !product) {
    setStatus("Sign in and select a product to list sessions.", true);
    return;
  }
  findSessionsBtn.disabled = true;
  setStatus("Loading previous sessions…");
  try {
    const { sessions } = await listSessions(product);
    sessionList.replaceChildren();
    if (sessions.length === 0) {
      const li = document.createElement("li");
      li.className = "hint";
      li.textContent = "No previous sessions for this product.";
      sessionList.appendChild(li);
    } else {
      for (const s of sessions) sessionList.appendChild(sessionRow(s, product));
    }
    sessionList.hidden = false;
    setStatus("");
  } catch (err) {
    setStatus(describeError(err), true);
  } finally {
    findSessionsBtn.disabled = false;
  }
});

// Enable the actions only once a product is actually chosen.
productSelect.addEventListener("change", () => {
  const has = Boolean(productSelect.value);
  openBtn.disabled = !has;
  findSessionsBtn.disabled = !has;
  sessionList.hidden = true;
});

// ---- Auth (GitHub) ----

githubLogin.addEventListener("click", () => loginWithGithub());
microsoftLogin.addEventListener("click", () => loginWithMicrosoft());

// Sign out: drop the GitHub token and any saved session, back to signed-out.
signOutBtn.addEventListener("click", () => {
  clearToken();
  clearSession();
  location.reload();
});

/** Fill the product dropdown with the member's accessible products. */
function populateProducts(): void {
  productSelect.replaceChildren();
  if (products.length === 0) {
    const o = document.createElement("option");
    o.value = "";
    o.textContent = "No products available for your account";
    productSelect.append(o);
    return;
  }
  for (const p of products) {
    const o = document.createElement("option");
    o.value = p.id;
    o.textContent = p.name;
    productSelect.append(o);
  }
  productSelect.disabled = false;
  // The first product is auto-selected - enable the actions right away.
  openBtn.disabled = false;
  findSessionsBtn.disabled = false;
}

async function initAuth(): Promise<void> {
  captureTokenFromHash();

  // A sign-in that produced no session says why, in the person's own terms.
  // Cancelling is not an error and is not phrased as one - they know what they
  // did and only need to see that nothing happened.
  const outcome = captureAuthOutcome();
  if (!getToken()) {
    setStatus(
      outcome === "cancelled"
        ? "Sign-in cancelled - nothing was changed. Choose a provider to try again."
        : outcome === "state"
          ? "That sign-in link had expired. Start again from this page."
          : outcome === "failed"
            ? "Sign-in could not be completed. Try again, or use the other provider."
            : "Sign in with GitHub or Microsoft to choose a product and start.",
    );
    return;
  }
  try {
    const profile = await me();
    signedInEmail = profile.email;
    products = profile.products;
    githubLogin.hidden = true;
    microsoftLogin.hidden = true;
    authRow.hidden = false;
    signedIn.textContent = `Signed in as ${profile.email}`;
    populateProducts();

    // Creating a product is a granted right, so the link exists only for the
    // accounts that hold it - and for those it is the only way to reach the
    // form, whether or not they already have products.
    createProductRow.hidden = !profile.can_create_products;

    // Having no products is a state to explain, not a page to be thrown at.
    // This used to redirect to the create form, which for most accounts is now
    // a form they may not use - two hops to reach a dead end.
    setStatus(
      products.length > 0
        ? ""
        : profile.can_create_products
          ? "You aren't linked to a product yet - create one to get started."
          : "You aren't linked to a product yet. Ask a product owner to invite you, " +
            "or open Doc-Support from BinDist to have one set up.",
    );
  } catch {
    clearToken();
    setStatus("Your session expired - please sign in again.", true);
  }
}
void initAuth();
